<template>
  <section v-if="fields && members.length" :class="rootClass" :style="rootStyle">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="c-leadership-team__grid">
        <article v-for="member in members" :key="member.id" class="c-leadership-team__card">
          <div class="c-leadership-team__media">
            <AdaptiveImage
              v-if="member.fields.photo"
              class="c-leadership-team__photo"
              :field="member.fields.photo"
              :show="true"
              :enable-scroll-detection="false"
              :show-image-redius="false"
            />
          </div>
          <div class="c-leadership-team__content">
            <div class="c-leadership-team__info">
              <JssText
                v-if="member.fields.role?.value"
                class="c-leadership-team__role"
                :field="member.fields.role"
                tag="h3"
              />
              <JssText
                v-if="member.fields.name?.value"
                class="c-leadership-team__name"
                :field="member.fields.name"
                tag="p"
              />
            </div>
            <RichText
              v-if="member.fields.description?.value"
              class="c-leadership-team__description"
              :field="member.fields.description"
            />
          </div>
        </article>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import type {
  LeadershipTeamMemberFields,
  LeadershipTeamProps,
} from '@/types/components/LeadershipTeam';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

interface LeadershipTeamMember extends ScItem<LeadershipTeamMemberFields> {
  fields: LeadershipTeamMemberFields;
}

const props = defineProps<LeadershipTeamProps>();
const fields = computed(() => props.fields);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-leadership-team'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const rawMembers = computed<ScItem<LeadershipTeamMemberFields>[]>(() => {
  const memberItems = utils.sc.getFieldObj(fields.value, 'members');
  return Array.isArray(memberItems) ? memberItems : [];
});

const members = computed<LeadershipTeamMember[]>(() =>
  rawMembers.value
    .map((member) => {
      const memberFields = member.fields;
      if (!memberFields) return null;

      return {
        ...member,
        fields: memberFields,
      };
    })
    .filter((member): member is LeadershipTeamMember => Boolean(member))
);
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-leadership-team {
  padding: 40px 0 56px;

  &__grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    column-gap: 36px;
    row-gap: 54px;
  }

  &__card {
    min-width: 0;
  }

  &__media {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    overflow: hidden;
    background: #eef4ef;
  }

  &__photo {
    width: 100%;
    height: 100%;
    display: block;

    .e-background-image {
      height: 100%;
    }
  }

  &__info {
    padding: 0 16px;
    min-height: 110px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: #efeadf;
  }

  &__role {
    margin: 0 0 10px;
    color: #30363a;
    font-size: 18px;
    line-height: 1.25;
    font-weight: 700;
    letter-spacing: 0.1px;
  }

  &__name {
    margin: 0 0 10px;
    color: #6f9a78;
    font-size: 16px;
    line-height: 1.35;
    font-weight: 700;
  }

  &__description {
    color: #3f4447;
    font-size: 13px;
    line-height: 1.52;
    margin-top: 23px;

    p {
      margin: 0;
    }

    strong,
    b {
      font-weight: 800;
    }
  }

  @media (max-width: 1199px) {
    &__grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      column-gap: 28px;
    }
  }

  @media (max-width: 767px) {
    padding: 32px 0 42px;

    &__grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 18px;
      row-gap: 34px;
    }

    &__role {
      font-size: 15px;
    }

    &__name {
      font-size: 14px;
    }

    &__description {
      font-size: 12px;
    }
  }

  @media (max-width: 479px) {
    &__grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
